<script setup>
    import NavBar from './NavBar.vue';
    import { ref, provide, onMounted, onUnmounted, computed } from 'vue';
    import { useRoute } from 'vue-router';
    import { toast } from 'vue3-toastify';
    import { Notificaciones as NotificacionesApi } from '../Api/API_Concentrador';
    import { alRecibirNotificacion, alReconectarNotificaciones, conectarTiempoReal, desconectarTiempoReal } from '../services/realtime.service';
    import {useAccountStore} from '../stores/account.store';
    import useAuthStore from '../stores/auth.store';
    import { useNotificationAlertsStore } from '../stores/notification-alerts.store';
    import { shouldShowAlert } from '../Utils/notification-alerts';
    import LeftBar from './LeftBar.vue';

    const route = useRoute();

    // Al cambiar la clave, la vista se rehace. Las listas antiguas dependen de eso
    // para releer sus filtros de la url, así que la clave sigue incluyéndolos; lo que
    // se excluye es el estado de interfaz (pestaña, paso), que la vista ya maneja sola.
    const estadoDeInterfaz = ['tab', 'paso', 'modo'];

    const claveVista = computed(() => {
        const filtros = Object.entries(route.query)
            .filter(([clave]) => !estadoDeInterfaz.includes(clave))
            .map(([clave, valor]) => `${clave}=${valor}`)
            .sort()
            .join('&');
        return `${route.path}?${filtros}`;
    });

    const htmlBar = ref(null);
    const ajustesVisibles = ref(false);
    const notificaciones = ref([]);
    const accountStore = useAccountStore();
    const authStore = useAuthStore();
    const alertsStore = useNotificationAlertsStore();

    provide('mostrarAjustes', ajustesVisibles);
    provide('notificaciones', notificaciones);

    /** Aviso del sistema operativo, cuando el usuario lo tiene permitido. */
    function notificacionNavegador(titulo, cuerpo) {
        if (!('Notification' in window)) return;
        if (Notification.permission === 'granted') {
            new Notification(titulo, { body: cuerpo ?? '' });
        } else if (Notification.permission !== 'denied') {
            Notification.requestPermission();
        }
    }

    let dejarDeEscuchar = null;
    let dejarDeEscucharReconexion = null;
    let montado = false;

    async function cargarRecientes() {
        try {
            const res = await NotificacionesApi.get();
            notificaciones.value = (res.data.data ?? []).slice(0, 30);
        } catch {
            // Se conserva la lista en memoria mientras vuelve la API.
        }
    }

    async function iniciarTiempoReal() {
        await cargarRecientes();
        if (!montado) return;

        dejarDeEscuchar = alRecibirNotificacion((evento) => {
            if (notificaciones.value.some((n) => n.id === evento.id)) return;
            notificaciones.value = [{
                id: evento.id,
                topic: evento.topic,
                resource: evento.resource,
                title: evento.title,
                body: evento.body,
                entityId: evento.entityId,
                processedAt: evento.occurredAt
            }, ...notificaciones.value].slice(0, 30);
            if (alertsStore.loaded) {
                const alerts = alertsStore.preferences;
                if (shouldShowAlert(evento, alerts.toast)) toast.info(`${evento.title}: ${evento.body || ''}`);
                if (shouldShowAlert(evento, alerts.desktop)) notificacionNavegador(evento.title, evento.body);
            }
        });

        dejarDeEscucharReconexion = alReconectarNotificaciones(cargarRecientes);

        await conectarTiempoReal();
    }

    onMounted(async () => {
        montado = true;
        // @ts-ignore
        htmlBar.value = document.getElementById('settings');
        await alertsStore.load(authStore.usuario?.id);
        if (montado) await iniciarTiempoReal();
    });

    onUnmounted(() => {
        montado = false;
        if (dejarDeEscuchar) dejarDeEscuchar();
        if (dejarDeEscucharReconexion) dejarDeEscucharReconexion();
        desconectarTiempoReal();
    });

</script>


<template>
    <NavBar />
    <LeftBar />
    <div class="content-page">
        <div class="content">
            <router-view :key="claveVista" v-slot="{ Component }">
                <component :is="Component" />
            </router-view>
        </div>
    </div>
</template>
